import { useParams, Link } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";
import ScrollToTop from "@/components/base/ScrollToTop";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import SchemaOrg from "@/components/base/SchemaOrg";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <h1 className="text-2xl font-bold font-heading text-foreground-950">分类不存在</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: category.name },
  ];

  const breadcrumbSchema = {
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首页",
        item: "https://91shequ.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: category.name,
        item: `https://91shequ.com/category/${category.slug}`,
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${category.name} - 91社区`,
          description: category.description,
          url: `https://91shequ.com/category/${category.slug}`,
        }}
      />

      <Navbar />
      <main className="min-h-screen pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <div className="py-8">
            <div className="flex items-center gap-4 mb-2">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className={`${category.icon} text-xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">{category.description}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pb-8">
            {categories.map((c) => (
              <Link
                key={c.id}
                to={`/category/${c.slug}`}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  c.slug === slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 border border-background-200/70"
                }`}
              >
                {c.name}
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 pb-16">
            {categoryArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group flex flex-col rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer overflow-hidden"
              >
                <div className="w-full h-44 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="flex flex-col flex-1 p-4">
                  <h2 className="text-sm md:text-base font-bold font-heading text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {article.title}
                  </h2>
                  <p className="mt-2 text-xs text-foreground-600 line-clamp-2 flex-1">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center gap-3 mt-3 pt-3 border-t border-background-200/50 text-xs text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i> {article.views.toLocaleString()}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-heart-line"></i> {article.likes.toLocaleString()}
                    </span>
                    <span>{article.publishDate}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {categoryArticles.length === 0 && (
            <div className="text-center py-20">
              <p className="text-foreground-600">该分类暂无文章</p>
              <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600">
                返回首页
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}